/* eslint-disable lingui/no-unlocalized-strings -- U02: legacy wire values and synthetic backend data; UI uses Lingui. */
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { defaultStringifySearch } from '@tanstack/react-router';
import { Download, ReceiptText } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { personListPath } from '@/modules/person/public';
import { ActionBar, defineAction, useActionDialogs, type ActionBarItem } from '@/shared/action';
import { stripDocumentSeparators } from '@/shared/format/internal';
import { formatCents } from '@/shared/format/money';
import { downloadBlob } from '@/shared/http';
import { BlockHostProvider, BlockRenderer, useNow } from '@/shared/page';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Select } from '@/shared/ui/select';

import { downloadBankSlipReceipt, saveBankSlipDownload } from './bank-slip.api';
import { bankSlipCodesBlock, bankSlipDetailsBlock, bankSlipHistoryBlock } from './bank-slip.blocks';
import { BankSlipFields } from './bank-slip.fields';
import {
  bankSlipFormDefaults,
  bankSlipFormDate,
  bankSlipFormSchema,
  otherInformationBody,
  type BankSlipFormValues,
} from './bank-slip.form';
import { bankSlipDetailQuery, useBankSlipMutation } from './bank-slip.queries';
import { bankSlipActionStates, type BankSlipAction } from './bank-slip.status-actions';
import type { BankSlip, BankSlipWalletContext } from './bank-slip.wire';

export const bankSlipActionLabels = {
  download: msg`Download Boleto`,
  protest: msg`Protestar`,
  cancelProtest: msg`Sustar Protesto`,
  cancel: msg`Solicitar baixa do boleto`,
  updateDueDate: msg`Atualizar vencimento`,
  updateOtherInformation: msg`Atualizar outros dados`,
  updateAmount: msg`Atualizar valor do boleto`,
  transfer: msg`Transferência de carteiras`,
  settlement: msg`Liquidar STR`,
  insertDeduction: msg`Inserir abatimento`,
  cancelDeduction: msg`Cancelar abatimento`,
  registerAgain: msg`Registrar novamente`,
};
type Context = { action: BankSlipAction; bankSlip: BankSlip };
export interface BankSlipPanelProps {
  readonly id: string;
  readonly billingStatus: string;
  readonly isRoot: boolean;
  readonly wallet?: BankSlipWalletContext;
  readonly onChanged?: () => void;
  readonly onNavigate?: (url: string) => void;
}

function useExecutors(bankSlip: BankSlip | undefined) {
  const updateDueDate = useBankSlipMutation('updateDueDate');
  const protest = useBankSlipMutation('protest');
  const cancel = useBankSlipMutation('cancel');
  const settlement = useBankSlipMutation('settlement');
  const insertDeduction = useBankSlipMutation('insertDeduction');
  const transfer = useBankSlipMutation('transfer');
  const cancelProtest = useBankSlipMutation('cancelProtest');
  const updateOtherInformation = useBankSlipMutation('updateOtherInformation');
  const updateAmount = useBankSlipMutation('updateAmount');
  const registerAgain = useBankSlipMutation('registerAgain');
  const cancelDeduction = useBankSlipMutation('cancelDeduction');
  const barCode = bankSlip?.barCode ?? '';
  return (action: BankSlipAction, v: BankSlipFormValues): Promise<unknown> => {
    switch (action) {
      case 'updateDueDate':
        return updateDueDate.mutateAsync({ barCode, dueDate: String(v['dueDate']) });
      case 'protest':
        return protest.mutateAsync({ barCode });
      case 'cancel':
        return cancel.mutateAsync({ barCode, keepBillingOpen: Boolean(v['keepBillingOpen']) });
      case 'settlement':
        return settlement.mutateAsync({
          barCode,
          body: {
            paidAmount: String(v['paidAmount']),
            confirmationTransactionTypeValue: String(v['confirmationTransactionTypeValue']),
            paymentISPB: v['paymentISPB'] == null ? null : Number(v['paymentISPB']),
            paymentAgency: v['paymentAgency'] == null ? null : Number(v['paymentAgency']),
            paymentDate: bankSlipFormDate(v['paymentDate'], v['originalPaymentDate']),
          },
        });
      case 'insertDeduction':
        return insertDeduction.mutateAsync({ barCode, deduction: String(v['deduction']) });
      case 'transfer':
        return transfer.mutateAsync({ barCode, toWalletCode: String(v['toWalletCode']) });
      case 'cancelProtest':
        return cancelProtest.mutateAsync({
          barCode,
          walletCode: bankSlip?.walletCode ?? 0,
          instructionTransactionTypeValue: v['keepBillingOpen']
            ? 'SustarProtestoManterCarteira'
            : 'SustarProtestoBaixarTitulo',
        });
      case 'updateOtherInformation':
        return updateOtherInformation.mutateAsync({ barCode, body: otherInformationBody(v) });
      case 'updateAmount':
        return updateAmount.mutateAsync({ barCode, amount: String(v['amount']) });
      case 'registerAgain':
        return registerAgain.mutateAsync({ barCode });
      case 'cancelDeduction':
        return cancelDeduction.mutateAsync({ barCode });
      case 'download':
        return Promise.resolve();
    }
  };
}

export function BankSlipPanel({
  id,
  billingStatus,
  isRoot,
  wallet,
  onChanged,
  onNavigate,
}: BankSlipPanelProps) {
  const { t, i18n } = useLingui();
  const query = useQuery(bankSlipDetailQuery(id));
  const bankSlip = query.data?.bankSlip;
  const execute = useExecutors(bankSlip);
  const dialogs = useActionDialogs();
  const now = useNow();
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [walletType, setWalletType] = useState('origin');
  const [updated, setUpdated] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const fees: Partial<
    Record<BankSlipAction, keyof NonNullable<BankSlipWalletContext['bankSlipCosts']>>
  > = {
    updateDueDate: 'changeDueDate',
    settlement: 'settlement',
    protest: 'debtCollection',
    cancel: 'cancellation',
    registerAgain: 'register',
    cancelProtest: 'debtCollectionCancellation',
    insertDeduction: 'discount',
  };
  const open = (name: BankSlipAction) => {
    if (!bankSlip) return;
    if (name === 'download') {
      setDownloadError(false);
      setWalletType('origin');
      setUpdated(false);
      setDownloadOpen(true);
      return;
    }
    const costKey = fees[name];
    const cost = costKey ? wallet?.bankSlipCosts?.[costKey] : undefined;
    const label = i18n._(bankSlipActionLabels[name]);
    const fee = cost == null ? '' : formatCents(cost, { locale: i18n.locale });
    const action = defineAction<Context, BankSlipFormValues>({
      id: `bankSlip.${name}`,
      kind: name === 'cancel' || name === 'cancelDeduction' ? 'destructive' : 'write',
      title: () => bankSlipActionLabels[name],
      summary: () =>
        cost == null
          ? t`Confirme a ação: ${label}.`
          : t`Confirme a ação: ${label}. Será cobrada uma tarifa de ${fee}.`,
      record: () => t`Boleto`,
      verb: bankSlipActionLabels[name],
      icon: ReceiptText,
      schema: () => bankSlipFormSchema(name, t`Preencha este campo`, t`Informe de 3 a 30 dias`),
      defaults: () => bankSlipFormDefaults(name, bankSlip, now),
      Fields: BankSlipFields,
      blocked: () =>
        costKey && wallet === undefined
          ? t`Dados da carteira indisponíveis para conferir a tarifa`
          : bankSlipActionStates(bankSlip, billingStatus, isRoot, now).some(
                (item) => item.id === name && item.visible && !item.disabled,
              )
            ? undefined
            : t`Ação indisponível para o estado atual`,
      submit: async (values) => (name === 'transfer' ? values : execute(name, values)),
      mobile: name === 'updateOtherInformation' ? 'screen' : 'sheet',
      // Os defaults são os mesmos para toda ação; só estas duas têm formulário de verdade (UUX §14).
      size: name === 'settlement' || name === 'updateOtherInformation' ? 'form' : 'confirm',
      ...(name === 'transfer' ? { success: false as const } : {}),
    });
    dialogs.open(action, { action: name, bankSlip }, (results) => {
      if (name !== 'transfer') {
        onChanged?.();
        return;
      }
      const values = results[0] as BankSlipFormValues;
      const destination = String(values['toWalletCode']);
      dialogs.open(
        defineAction<Context>({
          id: 'bankSlip.confirmTransfer',
          kind: 'write',
          title: () => msg`Transferência de carteiras`,
          summary: () => t`Confirme a transferência para a carteira ${destination}.`,
          record: () => t`Boleto`,
          verb: msg`transferir`,
          submit: () => execute('transfer', values),
        }),
        { action: name, bankSlip },
        () => onChanged?.(),
      );
    });
  };
  const actions: ActionBarItem[] = bankSlip
    ? bankSlipActionStates(bankSlip, billingStatus, isRoot, now)
        .filter((item) => item.visible)
        .map(({ id: actionId, disabled }) => ({
          id: `bankSlip.${actionId}`,
          label: bankSlipActionLabels[actionId],
          kind:
            actionId === 'download'
              ? 'read'
              : actionId === 'cancel' || actionId === 'cancelDeduction'
                ? 'destructive'
                : 'write',
          disabled:
            fees[actionId] && wallet === undefined
              ? t`Dados da carteira indisponíveis para conferir a tarifa`
              : disabled
                ? t`Disponível dois dias após o vencimento`
                : false,
          icon: actionId === 'download' ? Download : ReceiptText,
          onSelect: () => open(actionId),
        }))
    : [];
  const navigate = (url: string) => {
    if (onNavigate) onNavigate(url);
    else window.location.assign(url);
  };
  const host = {
    surface: 'screen' as const,
    navigate,
    run: (actionId: string, input: unknown) => {
      if (actionId === 'bankSlip.openBeneficiary' && bankSlip?.beneficiary?.registrationNumber) {
        // A lista de pessoas filtrada pelo documento do beneficiário, como o legado (`BankSlipFormContainer.tsx:93-99`):
        // 14 caracteres é PJ. A busca vai em JSON, como o roteador escreve, para o documento continuar texto.
        const registrationNumber = stripDocumentSeparators(bankSlip.beneficiary.registrationNumber);
        navigate(`${personListPath(registrationNumber.length === 14 ? 'legal' : 'natural')}${defaultStringifySearch({ registrationNumber })}`);
      }
      if (actionId === 'bankSlip.copy' && bankSlip) {
        const field = (input as { field: 'barCode' | 'numericLine' }).field;
        void navigator.clipboard
          .writeText(bankSlip[field])
          .then(() => toast.success(t`Copiado com sucesso`))
          .catch(() => toast.error(t`Não foi possível copiar`));
      }
      if (actionId === 'bankSlip.receipt')
        void downloadBankSlipReceipt((input as { id: string }).id)
          .then((file) => downloadBlob(file.blob, file.fileName))
          .catch(() => toast.error(t`Não foi possível baixar o recibo`));
    },
  };
  if (!id) return null;
  return (
    <section className="space-y-5 pb-20 md:pb-0" aria-label={t`Boleto`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold">{t`Boleto`}</h2>
        <ActionBar items={actions} label={t`Ações do boleto`} />
      </div>
      {bankSlip?.firstInstructionDebtCollection === '06' &&
        (bankSlip.secondInstructionDebtCollection ?? 0) > 0 && (
          <div className="rounded-lg border border-border bg-muted p-4 text-sm">
            <p>{t`Boleto possui instrução de protesto automático ${bankSlip.secondInstructionDebtCollection} dias após o vencimento.`}</p>
            <p>
              {wallet?.enableAutoSubmitToDebtCollection === undefined
                ? t`Configuração de protesto do convênio indisponível`
                : wallet.enableAutoSubmitToDebtCollection
                  ? t`Convênio habilitado para protesto automático`
                  : t`Convênio não está habilitado para protesto automático`}
            </p>
          </div>
        )}
      <BlockHostProvider host={host}>
        <BlockRenderer block={bankSlipDetailsBlock} params={{ id, isRoot }} />
        <BlockRenderer block={bankSlipCodesBlock} params={{ id, isRoot }} />
        <BlockRenderer block={bankSlipHistoryBlock} params={{ id, isRoot }} />
      </BlockHostProvider>
      {dialogs.element}
      <Dialog
        open={downloadOpen}
        onOpenChange={(value) => {
          if (!downloading) setDownloadOpen(value);
        }}
      >
        <DialogContent size="confirm">
          <DialogHeader>
            <DialogTitle>{t`Download boleto`}</DialogTitle>
            <DialogDescription>{t`Escolha a carteira e o valor do boleto para download.`}</DialogDescription>
          </DialogHeader>
          {bankSlip && bankSlip.walletCode !== bankSlip.walletCodeOrigem && (
            <Select
              aria-label={t`Carteira`}
              value={walletType}
              onChange={setWalletType}
              options={[
                { value: 'origin', label: t`Carteira de origem` },
                { value: 'current', label: t`Carteira atual` },
              ]}
            />
          )}
          <label className="flex min-h-12 items-center gap-4">
            <Checkbox
              checked={updated}
              onCheckedChange={(checked) => setUpdated(checked === true)}
            />
            {t`Boleto atualizado`}
          </label>
          {downloadError && (
            <p role="alert" className="text-destructive">{t`Não foi possível baixar o boleto`}</p>
          )}
          <DialogFooter>
            <Button
              variant="secondary"
              disabled={downloading}
              onClick={() => setDownloadOpen(false)}
            >{t`Fechar`}</Button>
            <Button
              disabled={downloading || !bankSlip}
              onClick={() => {
                if (!bankSlip) return;
                setDownloading(true);
                setDownloadError(false);
                void saveBankSlipDownload({
                  barCode: bankSlip.barCode,
                  walletCode: String(
                    walletType === 'origin' ? bankSlip.walletCodeOrigem : bankSlip.walletCode,
                  ),
                  useUpdated: updated,
                })
                  .then(() => {
                    setDownloadOpen(false);
                    toast.success(t`Download concluído com sucesso`);
                  })
                  .catch(() => setDownloadError(true))
                  .finally(() => setDownloading(false));
              }}
            >{t`Baixar boleto`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
